import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import config from "../config";
import "../Style/Navbar.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faExclamationTriangle,
  faTasks,
  faExchangeAlt,
  faChartLine,
  faBolt,
  faUndoAlt,
  faBook,
  faEllipsisV,
  faMoon,
  faSun,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";
import { authFetch } from "../utils/Security/authFetch";
import localforage from "localforage";
import { Calendar } from "react-big-calendar";

const iconMap: { [key: string]: any } = {
  home: faHome,
  "exclamation-triangle": faExclamationTriangle,
  tasks: faTasks,
  "exchange-alt": faExchangeAlt,
  "chart-line": faChartLine,
  bolt: faBolt,
  "undo-alt": faUndoAlt,
  book: faBook,
  calendar: faCalendar,
};

interface Tab {
  id: number;
  tabname: string;
  admin: boolean;
  user: boolean;
  parentTab: Tab | null;
  children: Tab[];
  icon?: string;
}

const Navbar: React.FC = () => {
  const username = localStorage.getItem("username");
  const isAdmin = localStorage.getItem("isAdmin");
  const userId = localStorage.getItem("userid");
  const navigate = useNavigate();
  const isForTelebras = localStorage.getItem("isForTelebras") === "true";
  const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
  const isForNilesat = localStorage.getItem("isForNilesat") === "true";

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Set<number>>(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [darkMode, setDarkMode] = useState<boolean>(true);

  const INCIDENT_CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;

  const dropdownRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    // Apply theme on initial load
    document.body.classList.toggle("dark-mode", darkMode);
    localStorage.setItem("darkMode", String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localforage.removeItem(INCIDENT_CACHE_KEY);
    const fetchTabs = async () => {
      try {
        const url =
          isAdmin === "true"
            ? `${config.apiBaseUrl}/api/tabs`
            : `${config.apiBaseUrl}/api/tabs/user`;
        const response = await authFetch(url);
        const contentType = response.headers.get("content-type");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Response is not JSON");
        }

        const data = await response.json();
        console.log("Fetched Tabs:", data);
        setTabs(data);
      } catch (error) {
        console.error("Failed to fetch tabs:", error);
      }
    };

    fetchTabs();
  }, [isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const toggleDropdown1 = () => setIsDropdownOpen((prev) => !prev);

  const toggleDropdown = (id: number) => {
    setOpenDropdowns((prev) => {
      const updated = new Set(prev);
      updated.has(id) ? updated.delete(id) : updated.add(id);
      return updated;
    });
  };

  const buildTabTree = (flatTabs: Tab[]) => {
    const tabMap = new Map<number, Tab>();
    flatTabs.forEach((tab) => {
      tab.children = [];
      tabMap.set(tab.id, tab);
    });

    const rootTabs: Tab[] = [];

    tabMap.forEach((tab) => {
      if (tab.parentTab && tabMap.has(tab.parentTab.id)) {
        tabMap.get(tab.parentTab.id)!.children.push(tab);
      } else {
        rootTabs.push(tab);
      }
    });

    // Sort root tabs and their children
    const sortTabs = (tabs: Tab[]) => {
      tabs.sort((a, b) => a.id - b.id);
      tabs.forEach((tab) => {
        if (tab.children.length > 0) {
          sortTabs(tab.children);
        }
      });
    };

    sortTabs(rootTabs);
    return rootTabs;
  };

  const renderTabTree = (tabsToRender: Tab[]) => {
    return tabsToRender.map((tab) => {
      const hasChildren = tab.children.length > 0;
      const iconKey = tab.icon?.toLowerCase() || "";
      return (
        <li key={tab.id}>
          {hasChildren ? (
            <button
              className="tab-button"
              onClick={() => toggleDropdown(tab.id)}
            >
              {iconMap[iconKey] && <FontAwesomeIcon icon={iconMap[iconKey]} />}{" "}
              {tab.tabname}
            </button>
          ) : (
            <Link
              to={`/${
                tab.tabname.toLowerCase().includes("rma")
                  ? "rma-request"
                  : tab.tabname.toLowerCase().includes("knowledge")
                    ? "knowledge-base"
                    : tab.tabname.toLowerCase()
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              {iconMap[iconKey] && <FontAwesomeIcon icon={iconMap[iconKey]} />}{" "}
              {tab.tabname}
            </Link>
          )}

          {hasChildren && openDropdowns.has(tab.id) && (
            <ul className="dropdown">{renderTabTree(tab.children)}</ul>
          )}
        </li>
      );
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("authToken");
    setIsDropdownOpen(false);
    navigate("/");
  };

  const renderTabs = () => {
    let filteredTabs = tabs;

    const isForTelebras = localStorage.getItem("isForTelebras") === "true";

     if (isAdmin === "true" && (isForTelebras || isForPetrobras || isForNilesat)) {
      const hiddenTabs = [
        "Calendar",
        "SLA",
        "admin",
        "Knowledge base",
        "Reporting",
      ];

      filteredTabs = tabs.filter(
        (tab) =>
          !hiddenTabs.some(
            (name) => tab.tabname.trim().toLowerCase() === name.toLowerCase(),
          ),
      );
    }

    const tabTree = buildTabTree(filteredTabs);
    return renderTabTree(tabTree);
  };
  return (
    <div className="navbar-container">
      <nav className="navbar">
        <button className="menu-btn" onClick={toggleSidebar}>
          ☰
        </button>
        <Link to="/home" style={{ textDecoration: "none" }}>
          <h1 className="logo">BDM</h1>
        </Link>

        <div className="rightside">
          {/* Username and theme toggle inline */}

          <div
            className="username-theme-wrapper clickable"
            onClick={toggleDropdown1}
          >
            <button
              className="theme-toggle-inline"
              onClick={(e) => {
                e.stopPropagation(); // prevent closing dropdown
                setDarkMode((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
            </button>
            {username && <span className="username-text">{username}</span>}

            <FontAwesomeIcon icon={faEllipsisV} className="dots-icon" />
          </div>

          {isDropdownOpen && (
            <ul className="user-dropdown" ref={dropdownRef}>
              {!isForTelebras && !isForPetrobras && !isForNilesat && isAdmin === "true" && (
                <li>
                  <Link
                    to="/superadmin"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Super Admin
                  </Link>
                </li>
              )}

              {!isForTelebras &&  !isForPetrobras && !isForNilesat && (
                <>
                  <li>
                    <Link
                      to="/profile"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link to="/update" onClick={() => setIsDropdownOpen(false)}>
                      Update
                    </Link>
                  </li>
                </>
              )}

              <li>
                <button onClick={handleLogout}>Logout</button>
              </li>
            </ul>
          )}
        </div>
      </nav>

      <div className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={toggleSidebar}>
          ×
        </button>
        <ul>{renderTabs()}</ul>
      </div>

      {isSidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
    </div>
  );
};

export default Navbar;
